import { store } from '@/app/store';
import type { TrackErrorsResponse } from '@/shared/types/api';
import { api, captureApiError, unwrap } from '../client';
import type { BinaryInput } from '../http';

// Skipped when no session_id (prevents 422 noise from null/undefined).
export function trackSessionErrors(
  sessionId: string | null | undefined,
  errorCodes: string[]
): Promise<TrackErrorsResponse | { skipped: true; reason: string }> {
  const validSessionId = sessionId ?? store.sessionId;
  if (!validSessionId) {
    console.warn('[api] Cannot track session errors: no session ID');
    return Promise.resolve({ skipped: true, reason: 'no_session_id' });
  }

  return unwrap(
    api.POST('/v1/sessions/{session_id}/analytics/errors', {
      params: { path: { session_id: validSessionId } },
      body: { data: errorCodes },
    })
  ).catch((error: Error) => {
    captureApiError(error, {
      endpoint: 'analytics_track_errors',
      extra: {
        // Full session ID is intentional Sentry metadata for DB joins.
        session_id: validSessionId,
        error_count: errorCodes?.length,
      },
      // A 401 ("Kiosk API key required") means this telemetry call was
      // made without a kiosk api_key — a customer-portal context that
      // should have used the download_code route. Capturing it just
      // re-floods Sentry (KIOSK-FRONTEND-Y*); the routing guard in
      // Analytics.trackError is the real fix.
      skipOnStatus: [401],
    });
    throw error;
  });
}

// Fire-and-forget: tracking failure should not break flow, but we still log
// to Sentry since kiosk silent failures go unnoticed.
function postPrintLockEvent<Body extends Record<string, string>>(
  body: Body,
  photoBlob: Blob | null,
  fileName: string,
  warning: string,
  endpoint: string,
  post: (
    body: Body,
    binaries: Record<string, BinaryInput>
  ) => Promise<{ data?: unknown }>
): Promise<unknown> {
  const binaries: Record<string, BinaryInput> = photoBlob
    ? { photo: { blob: photoBlob, name: fileName } }
    : {};
  return unwrap(post(body, binaries)).catch((error: Error) => {
    console.warn(warning, error?.message);
    captureApiError(error, {
      endpoint,
      extra: { ...body, has_photo: Boolean(photoBlob) },
    });
  });
}

export function trackPrintLock(
  slug: string,
  photoBlob: Blob | null = null
): Promise<unknown> {
  return postPrintLockEvent(
    { slug },
    photoBlob,
    'lock.jpg',
    '[api] trackPrintLock failed:',
    'admin_create_print_lock',
    (body, binaries) =>
      api.POST('/v1/admin/print-locks', { body, multipart: { binaries } })
  );
}

export function trackPrintUnlock(
  printLockUuid: string,
  photoBlob: Blob | null = null
): Promise<unknown> {
  return postPrintLockEvent(
    { record_id: printLockUuid },
    photoBlob,
    'unlock.jpg',
    '[api] trackPrintUnlock failed:',
    'admin_create_print_unlock',
    (body, binaries) =>
      api.POST('/v1/admin/print-unlocks', { body, multipart: { binaries } })
  );
}
